import { AxiosInstance, AxiosResponse } from "axios";
import ApiPath from "src/api/apiPath.json";
import { ApiResponse } from "../public-types";
import {
  ContractCredit,
  ContractCreditBucket,
  ContractCreditUpdateBody,
  CreditCharge,
  CreditChargeBody,
  ProductCredit,
  ProductCreditUpdateBody,
} from "./credit-types";

/**
 * RPP42. 상품 크레딧 조회
 * GET /api/ctrl/partners/{partnerId}/pr/products/{productId}/credit
 */
export const getProductCreditAsync = (
  axios: AxiosInstance,
  params?: { productId: number | string },
): Promise<AxiosResponse<ApiResponse<ProductCredit>>> => {
  const path = ApiPath.credit.productCredit.replace("{productId}", String(params!.productId));
  return axios.get<ApiResponse<ProductCredit>>(path);
};

/**
 * RPP41. 상품 크레딧 수정
 * PUT /api/ctrl/partners/{partnerId}/pr/products/{productId}/credit
 */
export const updateProductCreditAsync = (
  axios: AxiosInstance,
  params?: { productId: number | string; body: ProductCreditUpdateBody },
): Promise<AxiosResponse<ApiResponse<{ productId: string }>>> => {
  const path = ApiPath.credit.productCredit.replace("{productId}", String(params!.productId));
  return axios.put<ApiResponse<{ productId: string }>>(path, params!.body);
};

/**
 * RCL02. 계약 크레딧 조회
 * GET /api/ctrl/partners/{partnerId}/ct/contract/{contractId}/credit
 */
export const getContractCreditAsync = (
  axios: AxiosInstance,
  params?: { contractId: number | string },
): Promise<AxiosResponse<ApiResponse<ContractCredit>>> => {
  const path = ApiPath.credit.contractCredit.replace("{contractId}", String(params!.contractId));
  return axios.get<ApiResponse<ContractCredit>>(path);
};

/**
 * RCL01. 계약 크레딧 수정 (계약 상태 '이용승인'까지만 가능)
 * PUT /api/ctrl/partners/{partnerId}/ct/contract/{contractId}/credit
 * (BE가 POST 미지원, PUT만 지원)
 */
export const updateContractCreditAsync = (
  axios: AxiosInstance,
  params?: { contractId: number | string; body: ContractCreditUpdateBody },
): Promise<AxiosResponse<ApiResponse<{ contractId: string }>>> => {
  const path = ApiPath.credit.contractCredit.replace("{contractId}", String(params!.contractId));
  return axios.put<ApiResponse<{ contractId: string }>>(path, params!.body);
};

/**
 * RCL21. 계약 크레딧 월별 현황 조회 (정산년월 오름차순)
 * GET /api/ctrl/partners/{partnerId}/ct/contract/{contractId}/credit-buckets
 */
export const getContractCreditBucketsAsync = (
  axios: AxiosInstance,
  params?: { contractId: number | string },
): Promise<AxiosResponse<ApiResponse<{ contractCreditBucketList: ContractCreditBucket[] }>>> => {
  const path = ApiPath.credit.creditBuckets.replace("{contractId}", String(params!.contractId));
  return axios.get<ApiResponse<{ contractCreditBucketList: ContractCreditBucket[] }>>(path);
};

/**
 * RCL22. 계약 크레딧 충전(회수) 이력 조회 (충전일시 내림차순)
 * GET /api/ctrl/partners/{partnerId}/ct/contract/{contractId}/credit-buckets/{bucketId}/charges
 */
export const getCreditChargesAsync = (
  axios: AxiosInstance,
  params?: { contractId: number | string; bucketId: number | string },
): Promise<AxiosResponse<ApiResponse<{ creditChargeList: CreditCharge[] }>>> => {
  const path = ApiPath.credit.creditBucketCharges
    .replace("{contractId}", String(params!.contractId))
    .replace("{bucketId}", String(params!.bucketId));
  return axios.get<ApiResponse<{ creditChargeList: CreditCharge[] }>>(path);
};

/**
 * RCL23. 계약 크레딧 충전(회수) 처리 (회수 시 chargedCredit 음수)
 * PUT /api/ctrl/partners/{partnerId}/ct/contract/{contractId}/credit-buckets/{bucketId}/charges
 * (BE가 POST 미지원, PUT만 지원)
 */
export const createCreditChargeAsync = (
  axios: AxiosInstance,
  params?: { contractId: number | string; bucketId: number | string; body: CreditChargeBody },
): Promise<AxiosResponse<ApiResponse<{ creditChargeId: string }>>> => {
  const path = ApiPath.credit.creditBucketCharges
    .replace("{contractId}", String(params!.contractId))
    .replace("{bucketId}", String(params!.bucketId));
  return axios.put<ApiResponse<{ creditChargeId: string }>>(path, params!.body);
};
